import type { BadgeColor } from '@/lib/base/badges/badges';
import { BadgeWithDot } from '@/lib/base/badges/badges';
import { Tooltip, TooltipTrigger } from '@/lib/base/tooltip/tooltip';
import type {
  LinkedInDeliveryState,
  LinkedInDeliveryStatus,
} from '@/lib/proposals/linkedin/deliveryTypes';
import { formatRelativeTime } from '@/lib/utils/timeFormatting';

interface DeliveryStatusBadgeProps {
  state: LinkedInDeliveryState;
}

interface StateConfig {
  label: string;
  color: BadgeColor<'pill-color'>;
  tooltip: string;
}

// Static per-status presentation. `sent`'s label gains a formatted
// `sent_at` suffix, computed per-instance below, not here.
const STATE_CONFIG: Record<LinkedInDeliveryStatus, StateConfig> = {
  queued: {
    label: 'Queued',
    color: 'gray',
    tooltip: 'Sending over the next few minutes',
  },
  claimed: {
    label: 'Sending',
    color: 'blue',
    tooltip: 'A browser is delivering this now',
  },
  sent: {
    label: 'Sent',
    color: 'success',
    tooltip: '',
  },
  failed: {
    label: 'Failed',
    color: 'error',
    tooltip: 'Send failed',
  },
  expired: {
    label: 'Expired · not sent',
    color: 'warning',
    tooltip: 'No browser delivered this within the window; it was not sent',
  },
};

// Unknown-status fallback — required so a new-BE/old-FE skew (a status this
// build doesn't know about) can never crash the action-card header.
const FALLBACK_CONFIG: StateConfig = {
  label: 'Delivery status',
  color: 'gray',
  tooltip: '',
};

/**
 * Maps a LinkedIn outbound `LinkedInDeliveryState` to a badge conveying its
 * delivery status. Wraps the badge in a tooltip when there's context worth
 * surfacing (why it's queued, the expiry reason).
 */
export function DeliveryStatusBadge({ state }: DeliveryStatusBadgeProps) {
  const cfg = STATE_CONFIG[state.status] ?? FALLBACK_CONFIG;

  const label =
    state.status === 'sent' && state.sent_at
      ? `Sent ${formatRelativeTime(state.sent_at)}`
      : cfg.label;

  const { tooltip } = cfg;

  const needsAttentionAriaLabel = state.needs_attention
    ? `${label} – needs attention`
    : undefined;

  const badge = (
    <BadgeWithDot size='sm' color={cfg.color}>
      {label}
    </BadgeWithDot>
  );

  if (!tooltip) {
    return badge;
  }

  return (
    <Tooltip title={tooltip} placement='top'>
      <TooltipTrigger aria-label={needsAttentionAriaLabel}>
        {badge}
      </TooltipTrigger>
    </Tooltip>
  );
}
